import { type ElementBase, type QuestionBase } from '@myre/ui-primitive-forms';
import { type Tooltip } from '@myre/ui-primitive-tooltip-directive';

import {
  type ComponentRef,
  type EnvironmentInjector,
  type Injector,
  type InputSignal,
  type Signal,
  type Type,
} from '@angular/core';
import { type UntypedFormGroup } from '@angular/forms';
import { type Observable } from 'rxjs';

export interface IConstructorModalContext {
  parent?: any;
  /**
   * Id of the body's `<form>` (`myre-form-id`). Not the modal's identity —
   * that is {@link IConstructorModalData.key}, its sibling one level up.
   */
  key?: string;
  formGroup?: UntypedFormGroup;
  elements?: ElementBase[] | Observable<ElementBase[]>;
  yesNoText?: string;
  onSubmit?: (values: any, questions: QuestionBase<any>[]) => any;
  cancelClick?: () => any;
  closeClick?: () => any;
  noClick?: () => any;
  yesClick?: () => any;
  deleteClick?: () => any;
  actionClick?: () => any;
  onClose?: () => any;
}

/** A modal-specific button sitting next to `ok`, wired to `actionClick`. */
export interface IActionOptions {
  label: string;
  /** Raw icon class. Not `myre-icon-sync`: the button spins it as its loading indicator. */
  icon?: string;
  loading?: Signal<boolean>;
  disabled?: Signal<boolean>;
}

export interface IDeleteOptions {
  label: string;
  withIcon?: boolean;
  onlyIcon?: boolean;
}

export interface IModalContext extends IConstructorModalContext {
  elements?: Observable<ElementBase[]>;
}

export interface IModalComponent {
  uid: InputSignal<string>;
  key?: string;
  context?: IConstructorModalContext;
  data?: any;
  form?: any;
  isLoading$?: Observable<boolean>;
}

// Data
export interface ModalFooterData {
  cancel?: string;
  delete?: IDeleteOptions;
  close?: string;
  no?: string;
  yes?: string;
  ok?: string;
  confirmWarning?: string;
  confirmInfo?: string;
  action?: IActionOptions;
  okLoading?: Signal<boolean>;
  /** Shown while `ok` is disabled, to say what is missing. */
  okTooltip?: Tooltip;
}

export interface ModalHeaderData {
  title: string;
}

// Bindings
export enum ModalSectionKey {
  Header = 'header',
  Body = 'body',
  Footer = 'footer',
}

export interface ModalBindableSection<D = Record<string, unknown>> {
  data?: D;
  component?: Type<any>;
  bindings?: ModalBindings;
  componentRef?: ComponentRef<any>;
}

export type ModalOutputBindingFn<T = unknown> = (event: T) => unknown;

export type ModalInputBindings = Record<string, unknown>;

export type ModalOutputBindings = Record<string, ModalOutputBindingFn>;

export interface ModalBindings {
  inputs?: ModalInputBindings;
  outputs?: ModalOutputBindings;
}

// Modal data
export interface IConstructorModalData {
  context: IConstructorModalContext;

  header?: ModalBindableSection<ModalHeaderData>;

  body?: {
    elementInjector?: Injector;
    environmentInjector?: EnvironmentInjector;
  } & ModalBindableSection;

  footer?: ModalBindableSection<ModalFooterData>;

  uid?: string;
  /**
   * Stable, human-readable identity of the modal, rendered as
   * `myre-modal-key` and as `data-testid` — the E2E selector, and the only
   * thing that can attribute a footer click or an abandon to one modal rather
   * than another.
   *
   * `<feature>.<intent>`, lowercase kebab segments (`property.delete`,
   * `debt.setup-fees.upload-schedule`). No `modal` in the name — it is one.
   * Unique within an app: two modals sharing a key are indistinguishable
   * everywhere the key is read. Never interpolate an entity id or name.
   *
   * Required, so a new modal cannot be born anonymous.
   */
  key: string;
  /**
   * Modal width. Maps to fixed pixel widths (see `modal.component.scss`):
   * - `small`  → 600px (default)
   * - `medium` → 900px
   * - `large`  → 1200px
   * - `xlarge` → 1350px
   * - `full`   → 100%
   *
   * All sizes are capped at `100vw` minus the page gutter on narrow screens.
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'full';
  type?: 'default' | 'sidebar' | 'warning' | 'info' | 'other';
  sidebarFrom?: 'left' | 'right';
  sidebarWidth?: number;
  sidebarOffset?: number;
  backgroundColor?: string;
  hidden?: boolean;
  /**
   * Raises the wrapper above app overlays that sit beyond the design-system
   * scale (e.g. the chat window). The wrapper applies the max across open
   * modals; omitted → the standard $z-index-modal.
   */
  zIndex?: number;
}

export interface IModalData extends IConstructorModalData {
  context: IModalContext;
}

export interface IOptionsYesNoModal {
  text: string;
  /** See {@link IConstructorModalData.key}. */
  key: string;
  onSubmitFct: (values: any) => void;
  onCancelFct?: () => void;
  onDismissFct?: () => void;
  type?: 'warning' | 'info' | 'other';
  elements?: ElementBase[];
  closeOnSubmit?: boolean;
  yesText?: string;
  noText?: string;
}
